import type { ComponentType, PropsWithChildren } from 'react';
import type React from 'react';

import type { ErrorStateDetails } from '#src/app/shared/utils/async';
import { isError, isLoaded } from '#src/app/shared/utils/async';

import type {
	PetitionDetailsPageCombinedState,
	PetitionDetailsPagePrefetchedContext,
	PetitionDetailsPagePrefetchedUserContext,
} from './hook';
import { usePageContext } from './hook';
import { PetitionDetailsPageDataContextProvider } from './pageData';
import { PetitionDetailsPageStateContextProvider } from './pageState';
import { PetitionDetailsPageUserDataContextProvider } from './pageUserData';

export type ErrorHandlerProps = {
	context: ErrorStateDetails<PetitionDetailsPageCombinedState> | { error: 'user_info_load_failure'; cause?: unknown };
};

type Props = {
	slug: string;
	prefetchedData: PetitionDetailsPagePrefetchedContext | undefined;
	prefetchedUserData: PetitionDetailsPagePrefetchedUserContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export const PetitionDetailsPageContextProvider: React.FC<PropsWithChildren<Props>> = ({
	children,
	slug,
	prefetchedData,
	prefetchedUserData,
	errorHandler: ErrorHandler,
	loadingFallback,
}) => {
	const pageContextState = usePageContext(slug, prefetchedData, prefetchedUserData);

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData, pageUserData } = pageContextState;

	return (
		<PetitionDetailsPageDataContextProvider pageData={pageData}>
			<PetitionDetailsPageUserDataContextProvider pageUserData={pageUserData}>
				<PetitionDetailsPageStateContextProvider>{children}</PetitionDetailsPageStateContextProvider>
			</PetitionDetailsPageUserDataContextProvider>
		</PetitionDetailsPageDataContextProvider>
	);
};
